import React, { useState, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import './MarkdownContent.css';
import { MathJaxContext } from 'better-react-mathjax';
import { copyToClipboard } from '../../utils/commonHelper';
import GenericTooltip from '../GenericTooltip/GenericTooltip';

// assets
import copyInactive from '../../assets/new-images/copy-icon.svg';
import copyActive from '../../assets/new-images/active-copy-icon.svg';

const CopyButton = ({ getText, dark = false, tooltipPosition = "bottom" }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        const textRef = getText();
        if (!textRef) return;

        // Handle both string and promise/async function
        const text = typeof textRef === 'function' ? await textRef() : textRef;

        const success = await copyToClipboard(text);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <GenericTooltip tooltipText={copied ? 'Copied!' : 'Copy'} tooltipPosition={tooltipPosition}>
            <button
                className={`markdown-copy-button ${copied ? 'copied' : ''}`}
                onClick={handleCopy}
                aria-label={copied ? 'Copied' : 'Copy'}
            >
                <img
                    src={copied ? copyActive : copyInactive}
                    alt={copied ? "Copied" : "Copy"}
                />
            </button>
        </GenericTooltip>
    );
};

const StreamingContext = React.createContext({ isStreaming: false, contentLength: 0 });

// Context to signal that a CodeBlock is rendered inside a TableBlock
const InsideTableContext = React.createContext(false);

const InsidePreContext = React.createContext(false);

const CodeBlock = ({ node, className, children, ...props }) => {
    const isInsidePre = React.useContext(InsidePreContext);

    if (!isInsidePre) {
        return (
            <code className={`inline-code ${className || ''}`} {...props}>
                {children}
            </code>
        );
    }

    return (
        <code className={className} {...props}>
            {children}
        </code>
    );
};

const PreBlock = ({ node, children, enableCopy, ...props }) => {
    const { isStreaming, contentLength } = React.useContext(StreamingContext);
    const insideTable = React.useContext(InsideTableContext);

    // Check if block is complete: either not streaming, or the node ends before the total content length
    const isComplete = !isStreaming || (node?.position?.end?.offset !== undefined && node.position.end.offset < contentLength);
    // Do NOT show copy button when inside a table — the table already has its own copy button
    const showCopy = enableCopy && isComplete && !insideTable;

    const getCodeText = () => {
        const codeElement = React.Children.toArray(children).find(
            child => React.isValidElement(child)
        );
        if (codeElement && codeElement.props && codeElement.props.children) {
            return String(codeElement.props.children).replace(/\n$/, '');
        }
        return '';
    };

    const content = (
        <InsidePreContext.Provider value={true}>
            <pre {...props}>
                {children}
            </pre>
        </InsidePreContext.Provider>
    );

    if (showCopy) {
        return (
            <div className="code-block-wrapper">
                <CopyButton getText={getCodeText} tooltipPosition="bottom" />
                {content}
            </div>
        );
    }

    return content;
};

const TableBlock = ({ node, children, enableCopy, ...props }) => {
    const tableRef = useRef(null);
    const { isStreaming, contentLength } = React.useContext(StreamingContext);

    const getTableText = () => {
        if (!tableRef.current) return '';

        // Simple tab-separated values for text copy
        const rows = Array.from(tableRef.current.querySelectorAll('tr'));
        const text = rows.map(row => {
            const cells = Array.from(row.querySelectorAll('th, td'));
            return cells.map(cell => cell.innerText).join('\t');
        }).join('\n');

        return {
            text: text,
            html: tableRef.current.outerHTML
        };
    };

    const isComplete = !isStreaming || (node?.position?.end?.offset !== undefined && node.position.end.offset < contentLength);
    const showCopy = enableCopy && isComplete;

    if (showCopy) {
        return (
            <InsideTableContext.Provider value={true}>
                <div className="table-block-wrapper">
                    <div className="table-copy-container">
                        <CopyButton getText={getTableText} tooltipPosition="bottom" />
                    </div>
                    <div className="table-content-container">
                        <table ref={tableRef} {...props}>
                            {children}
                        </table>
                    </div>
                </div>
            </InsideTableContext.Provider>
        );
    }

    return (
        <InsideTableContext.Provider value={true}>
            <div className="table-block-wrapper">
                <div className="table-content-container">
                    <table ref={tableRef} {...props}>
                        {children}
                    </table>
                </div>
            </div>
        </InsideTableContext.Provider>
    );
};

const MarkdownContent = ({ content, className = '', isStreaming, useMathJax = false, enableSectionCopy = false }) => {

    const components = useMemo(() => ({
        code: (props) => <CodeBlock {...props} />,
        pre: (props) => <PreBlock {...props} enableCopy={enableSectionCopy} />,
        table: (props) => <TableBlock {...props} enableCopy={enableSectionCopy} />
    }), [enableSectionCopy]);

    const markdownComponent = (
        <div className={`${className} ${isStreaming ? 'streaming' : ''}`.trim()}>
            <StreamingContext.Provider value={{ isStreaming, contentLength: content?.length || 0 }}>
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={components}
                >
                    {content}
                </ReactMarkdown>
            </StreamingContext.Provider>
        </div>
    );

    if (useMathJax) {
        return (
            <MathJaxContext
                version={3}
                config={{
                    loader: { load: ["[tex]/ams"] },
                    tex: {
                        packages: { "[+]": ["ams"] },
                        inlineMath: [
                            ["\\(", "\\)"],
                            ["$", "$"],
                        ],
                        displayMath: [
                            ["\\[", "\\]"],
                            ["$$", "$$"],
                        ],
                    },
                }}
            >
                {markdownComponent}
            </MathJaxContext>
        );
    }

    return markdownComponent;
};

export default MarkdownContent;
